import { ConfirmDialog } from "@connected-repo/ui-mui/components/ConfirmDialog";
import { LoadingSpinner } from "@connected-repo/ui-mui/components/LoadingSpinner";
import { SamplePlayButton } from "@connected-repo/ui-mui/components/SamplePlayButton";
import { Chip } from "@connected-repo/ui-mui/data-display/Chip";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@connected-repo/ui-mui/data-display/Table";
import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { Alert } from "@connected-repo/ui-mui/feedback/Alert";
import { Button } from "@connected-repo/ui-mui/form/Button";
import { MenuItem } from "@connected-repo/ui-mui/form/MenuItem";
import { TextField } from "@connected-repo/ui-mui/form/TextField";
import { ToggleButton } from "@connected-repo/ui-mui/form/ToggleButton";
import { ToggleButtonGroup } from "@connected-repo/ui-mui/form/ToggleButtonGroup";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { Card } from "@connected-repo/ui-mui/layout/Card";
import { Stack } from "@connected-repo/ui-mui/layout/Stack";
import {
	languageLabel,
	STAGE_SESSION_LANGUAGES,
	stageSessionLanguageZod,
} from "@connected-repo/zod-schemas/languages.zod";
import {
	defaultVoiceFor,
	type Voice,
	type VoiceGender,
	voicesTunedFor,
} from "@connected-repo/zod-schemas/voice.zod";
import { orpc, orpcFetch } from "@superadmin/utils/orpc.client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "react-toastify";
import { z } from "zod";

const genderFilterZod = z.enum(["all", "female", "male"]).catch("all");
/** Empty: the global scope — defaults for every language, samples in English. */
const scopeLanguageZod = stageSessionLanguageZod.catch("");

/**
 * Voices (root ADR-032): voicely's whole roster — every Soniox voice, each
 * with the languages its accent is tuned for — as every desk is offered
 * it, with what ConferHall adds: a default voice per gender, globally and
 * per language, and a sample clip per voice per language that anyone's
 * play button makes on first ask and only this page can make afresh.
 *
 * One language select scopes the page: the defaults shown and set are
 * that language's (falling back to the global pair), samples play in it,
 * and the roster leads with the voices tuned for it. The scope, the
 * gender filter and the "all voices" toggle live in the URL so a refresh
 * keeps them.
 */
export default function VoicesPage() {
	const queryClient = useQueryClient();
	const [params, setParams] = useSearchParams();
	const scope = scopeLanguageZod.parse(params.get("lang") ?? "");
	const gender = genderFilterZod.parse(params.get("gender") ?? undefined);
	const showAll = params.get("all") === "1";
	const setParam = (key: string, value: string | null) => {
		const copy = new URLSearchParams(params);
		if (value === null) copy.delete(key);
		else copy.set(key, value);
		setParams(copy, { replace: true });
	};
	// Samples speak the scoped language; English stands in for "global".
	const sampleLanguage = scope || "en";
	// The act waiting on a yes: clearing a default, remaking a sample.
	const [asking, setAsking] = useState<{
		title: string;
		description: string;
		confirmLabel: string;
		run: () => void;
	} | null>(null);

	const catalog = useQuery(orpc.voiceCatalog.queryOptions({ input: {}, retry: false }));
	const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.voiceCatalog.key() });
	const setDefault = useMutation(
		orpc.setDefaultVoice.mutationOptions({
			onSuccess: async (_result, input) => {
				await refresh();
				const where = input.language ? `for ${languageLabel(input.language)}` : "globally";
				toast.success(
					input.voiceId
						? `${input.voiceId} is the default ${input.gender} voice ${where}`
						: `No default ${input.gender} voice ${where}`,
				);
			},
			onError: (err) => toast.error(err.message),
		}),
	);
	const regenerate = useMutation(
		orpc.voiceSample.mutationOptions({
			onSuccess: async (made) => {
				await refresh();
				toast.success(`Remade ${made.voiceId}'s ${made.language} sample`);
			},
			onError: (err) => toast.error(err.message),
		}),
	);

	// The clip for a voice in the scoped language: kept, else made now.
	const sample = async (voice: Voice): Promise<string> => {
		const kept = voice.samples[sampleLanguage];
		if (kept) return kept;
		const made = await orpcFetch.voiceSample({ voiceId: voice.id, language: sampleLanguage, regenerate: false });
		void refresh();
		return made.url;
	};

	const voices = catalog.data?.voices ?? [];
	const tuned = scope ? voicesTunedFor(voices, scope) : [];
	const onlyTuned = scope !== "" && !showAll && tuned.length > 0;
	const shown = (onlyTuned ? tuned : voices).filter((v) => gender === "all" || v.gender === gender);
	const sampled = voices.filter((v) => v.samples[sampleLanguage]).length;
	const isTuned = (v: Voice) => scope !== "" && v.languages.includes(scope);
	// The scope's own defaults, and what applies after the fallback.
	const own = (g: VoiceGender): string | null =>
		scope ? (catalog.data?.languageDefaults[scope]?.[g] ?? null) : (catalog.data?.defaults[g] ?? null);
	const effective = (g: VoiceGender): string | null =>
		catalog.data ? defaultVoiceFor(catalog.data.defaults, catalog.data.languageDefaults, scope || "", g) : null;

	return (
		<Stack spacing={3}>
			<Box>
				<Typography variant="h4" sx={{ fontWeight: 800 }}>
					Voices
				</Typography>
				<Typography color="text.secondary">
					Every voice voicely offers is offered at the desk. Each is recorded with an accent, so the desk
					leads with the voices tuned for the language a side hears; every voice can still speak every
					language. A session side that names no voice speaks in a man's voice suited to its language:
					the male default set here for that language, else the male voice tuned most closely for it, else
					the global male default (a woman's voice the same way only when no man's is on offer). A sample
					is synthesised the first time anyone plays a voice in a language and kept on our storage; only
					this page can make one afresh.
				</Typography>
			</Box>

			<Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap", gap: 1.5 }}>
				<TextField
					select
					size="small"
					label="Language"
					value={scope}
					onChange={(e) => {
						setParam("lang", e.target.value || null);
					}}
					sx={{ minWidth: 260 }}
					helperText={
						scope
							? `Defaults for ${languageLabel(scope)}; samples in it; ${tuned.length} tuned voice${tuned.length === 1 ? "" : "s"}.`
							: "Global defaults, every voice, samples in English."
					}
				>
					<MenuItem value="">Global — every language</MenuItem>
					{STAGE_SESSION_LANGUAGES.map((l) => (
						<MenuItem key={l.code} value={l.code}>
							{l.label} ({l.native})
						</MenuItem>
					))}
				</TextField>
			</Stack>

			{catalog.data && (
				<Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", p: 2 }}>
					<Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
						Defaults {scope ? `for ${languageLabel(scope)}` : "for every language"}
					</Typography>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "minmax(0,1fr) minmax(0,1fr)" },
							gap: 2,
						}}
					>
						{(["female", "male"] as const).map((g) => (
							<DefaultCard
								key={g}
								gender={g}
								scope={scope}
								own={voices.find((v) => v.id === own(g)) ?? null}
								effective={voices.find((v) => v.id === effective(g)) ?? null}
								fallback={g === "female" ? catalog.data.defaultVoiceId : null}
								sample={sample}
								saving={setDefault.isPending}
								onClear={() =>
									setAsking({
										title: `Clear the default ${g} voice ${scope ? `for ${languageLabel(scope)}` : "globally"}?`,
										description: scope
											? `Sessions that name no ${g} voice for ${languageLabel(scope)} fall back to the global default.`
											: `Sessions that name no ${g} voice, in a language with no default of its own, fall back to voicely's choice.`,
										confirmLabel: "Clear",
										run: () => setDefault.mutate({ gender: g, voiceId: null, language: scope || null }),
									})
								}
							/>
						))}
					</Box>
				</Card>
			)}

			<Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflowX: "auto" }}>
				<Stack
					direction="row"
					spacing={2}
					alignItems="center"
					sx={{ p: 2, pb: 1, flexWrap: "wrap", gap: 1.5 }}
				>
					<Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
						Voicely's roster
						{catalog.data ? ` · ${sampled} of ${voices.length} sampled in ${languageLabel(sampleLanguage)}` : ""}
					</Typography>
					{scope && tuned.length > 0 && (
						<ToggleButtonGroup
							size="small"
							exclusive
							value={showAll ? "all" : "tuned"}
							onChange={(_, next: string | null) => next && setParam("all", next === "all" ? "1" : null)}
							aria-label="Which voices"
						>
							<ToggleButton value="tuned">Tuned for {languageLabel(scope)}</ToggleButton>
							<ToggleButton value="all">All voices</ToggleButton>
						</ToggleButtonGroup>
					)}
					<ToggleButtonGroup
						size="small"
						exclusive
						value={gender}
						onChange={(_, next: string | null) => next && setParam("gender", next === "all" ? null : next)}
						aria-label="Filter by gender"
					>
						<ToggleButton value="all">Any gender</ToggleButton>
						<ToggleButton value="female">Female</ToggleButton>
						<ToggleButton value="male">Male</ToggleButton>
					</ToggleButtonGroup>
				</Stack>
				{scope && tuned.length === 0 && catalog.data && (
					<Alert severity="info" sx={{ mx: 2, mb: 1 }}>
						No voice is recorded with an accent suited to {languageLabel(scope)}; every voice can still speak
						it. Pick the defaults by ear.
					</Alert>
				)}
				{catalog.isLoading && <LoadingSpinner text="Asking voicely…" />}
				{catalog.error && (
					<Alert severity="warning" sx={{ m: 2 }}>
						{catalog.error.message}
					</Alert>
				)}
				{catalog.data?.rosterUnavailable && (
					<Alert severity="warning" sx={{ m: 2 }}>
						Voicely did not answer and nothing is cached yet; the roster is empty until it does.
					</Alert>
				)}
				{catalog.data && (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Voice</TableCell>
								<TableCell>Gender</TableCell>
								<TableCell>Accent</TableCell>
								<TableCell>Age</TableCell>
								<TableCell>Tuned for</TableCell>
								<TableCell>Description</TableCell>
								<TableCell align="center">Hear</TableCell>
								<TableCell>Samples</TableCell>
								<TableCell align="right">Default {scope ? `for ${languageLabel(scope)}` : ""}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{shown.map((v) => {
								const isDefault: VoiceGender | null =
									own("female") === v.id ? "female" : own("male") === v.id ? "male" : null;
								const languages = Object.keys(v.samples).sort();
								return (
									<TableRow key={v.id} sx={{ verticalAlign: "top", "& td": { py: 1.25 } }}>
										<TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
											{v.id}
											{isDefault && (
												<Chip
													label={isDefault === "female" ? "Default female" : "Default male"}
													size="small"
													color="primary"
													variant="outlined"
													sx={{ ml: 1, height: 20, fontSize: 11 }}
												/>
											)}
											{isTuned(v) && !isDefault && (
												<Chip
													label="Tuned"
													size="small"
													color="primary"
													variant="outlined"
													sx={{ ml: 1, height: 20, fontSize: 11 }}
												/>
											)}
										</TableCell>
										<TableCell>{v.gender ?? "—"}</TableCell>
										<TableCell sx={{ whiteSpace: "nowrap" }}>{v.accent?.replace("_", " ") ?? "—"}</TableCell>
										<TableCell sx={{ whiteSpace: "nowrap" }}>{v.age?.replace("_", " ") ?? "—"}</TableCell>
										<TableCell sx={{ fontSize: 12, color: "text.secondary", minWidth: 140 }}>
											{v.languages.length ? v.languages.map(languageLabel).join(", ") : "—"}
										</TableCell>
										<TableCell sx={{ color: "text.secondary", minWidth: 240 }}>{v.description ?? "—"}</TableCell>
										<TableCell align="center">
											<SamplePlayButton
												size={36}
												title={
													v.samples[sampleLanguage]
														? `Hear this voice in ${languageLabel(sampleLanguage)}`
														: `Make and hear this voice's ${languageLabel(sampleLanguage)} sample`
												}
												src={() => sample(v)}
												onError={(message) => toast.error(message)}
											/>
										</TableCell>
										<TableCell sx={{ minWidth: 160 }}>
											<Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }} alignItems="center">
												{languages.length === 0 && (
													<Typography sx={{ fontSize: 12, color: "text.secondary" }}>none yet</Typography>
												)}
												{languages.map((code) => (
													<Chip
														key={code}
														label={code}
														size="small"
														variant={code === sampleLanguage ? "filled" : "outlined"}
														sx={{ height: 20, fontSize: 11 }}
													/>
												))}
												{v.samples[sampleLanguage] && (
													<Button
														size="small"
														sx={{ fontSize: 11, minWidth: 0, px: 0.75 }}
														disabled={regenerate.isPending && regenerate.variables?.voiceId === v.id}
														onClick={() =>
															setAsking({
																title: `Remake ${v.id}'s ${sampleLanguage} sample?`,
																description:
																	"The kept clip is replaced by a new synthesis, paid for at voicely, and every picker plays the new one.",
																confirmLabel: "Remake",
																run: () =>
																	regenerate.mutate({ voiceId: v.id, language: sampleLanguage, regenerate: true }),
															})
														}
													>
														Remake {sampleLanguage}
													</Button>
												)}
											</Stack>
										</TableCell>
										<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
											{v.gender !== "female" && isDefault !== "male" && (
												<Button
													size="small"
													variant="outlined"
													disabled={setDefault.isPending}
													onClick={() => setDefault.mutate({ gender: "male", voiceId: v.id, language: scope || null })}
													sx={{ ml: 1 }}
												>
													Default male
												</Button>
											)}
											{v.gender !== "male" && isDefault !== "female" && (
												<Button
													size="small"
													variant="outlined"
													disabled={setDefault.isPending}
													onClick={() => setDefault.mutate({ gender: "female", voiceId: v.id, language: scope || null })}
													sx={{ ml: 1 }}
												>
													Default female
												</Button>
											)}
										</TableCell>
									</TableRow>
								);
							})}
							{shown.length === 0 && !catalog.data.rosterUnavailable && (
								<TableRow>
									<TableCell colSpan={9}>
										<Typography color="text.secondary" sx={{ py: 2 }}>
											No voices of that gender.
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				)}
			</Card>
			<ConfirmDialog
				open={!!asking}
				title={asking?.title ?? ""}
				description={asking?.description ?? ""}
				confirmLabel={asking?.confirmLabel ?? "Confirm"}
				onClose={() => setAsking(null)}
				onConfirm={() => {
					asking?.run();
					setAsking(null);
				}}
			/>
		</Stack>
	);
}

function DefaultCard({
	gender,
	scope,
	own,
	effective,
	fallback,
	sample,
	saving,
	onClear,
}: {
	gender: VoiceGender;
	/** The language the card is for; empty for the global pair. */
	scope: string;
	/** This scope's own default, when set. */
	own: Voice | null;
	/** What a session actually gets after the fallback: the scope's own, else the global. */
	effective: Voice | null;
	/** What applies when nothing is set anywhere; null when nothing does. */
	fallback: string | null;
	sample: (voice: Voice) => Promise<string>;
	saving: boolean;
	onClear: () => void;
}) {
	const shown = own ?? effective;
	const inherited = !own && effective !== null && scope !== "";
	return (
		<Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5, minWidth: 0 }}>
			<Typography sx={{ fontSize: 12, color: "text.secondary" }}>
				{gender === "female" ? "Female" : "Male"} default
				{inherited ? " · from the global default" : ""}
			</Typography>
			<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
				<Box sx={{ flexGrow: 1, minWidth: 0 }}>
					<Typography sx={{ fontWeight: 700 }} noWrap>
						{shown ? shown.id : fallback ? `None set — voicely's ${fallback}` : "None set"}
					</Typography>
					<Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: "19px" }}>
						{shown
							? [shown.accent?.replace("_", " "), shown.age?.replace("_", " "), shown.description]
									.filter(Boolean)
									.join(" · ")
							: gender === "female"
								? "A session that names no voice is spoken in this one."
								: "The one-click alternative a desk is offered."}
					</Typography>
					{shown && scope && shown.languages.length > 0 && !shown.languages.includes(scope) && (
						<Typography sx={{ fontSize: 12, color: "warning.main", mt: 0.5 }}>
							Not tuned for {languageLabel(scope)}: recorded with {shown.accent?.replace("_", " ")} speech.
						</Typography>
					)}
				</Box>
				<Box sx={{ flexShrink: 0 }}>
					<SamplePlayButton
						size={36}
						disabled={!shown}
						unavailable="Set a default to hear it"
						src={() => (shown ? sample(shown) : Promise.resolve(null))}
						onError={(message) => toast.error(message)}
					/>
				</Box>
				{own && (
					<Button size="small" disabled={saving} onClick={onClear} sx={{ flexShrink: 0 }}>
						Clear
					</Button>
				)}
			</Stack>
		</Box>
	);
}
